import { useState, useEffect, useCallback, useRef } from 'react';
import webSocketService, { WebSocketConnectionStatus } from '../services/webSocketService.ts';
import { AudioPlayer } from '../utils/AudioPlayer.ts';
import config from '../../../config';
import {
  arrayBufferToBase64,
  base64ToFloat32Array,
  float32To16BitPCM,
  isFirefox,
  TARGET_SAMPLE_RATE
} from '../utils/audioUtils.ts';

// Import debug helpers for browser console filtering
import '../utils/debugHelpers.ts';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-voiceagent');

// Add debug flags at the top after imports
const TEXT_DEBUG = true; // Set to false to reduce text logs
const AUDIO_DEBUG = false; // Set to true to see audio logs
const AUDIO_PACKET_DEBUG = false; // Set to true to see audio packet logs

// Basic Chat Message Interface
export interface ChatMessage {
  id: string; // Unique ID for each message
  role: 'USER' | 'ASSISTANT' | 'SYSTEM' | 'ERROR';
  content: string;
  timestamp: Date;
  turnId?: string; // Track conversation turn for proper message grouping (optional for SYSTEM/ERROR)
  latency?: number; // Optional latency for assistant messages
  isPartial?: boolean; // For incrementally built messages
}

// Voice Agent Hook State Interface
interface VoiceAgentState {
  connectionStatus: WebSocketConnectionStatus;
  isStreaming: boolean;
  chatMessages: ChatMessage[];
  isMicrophoneEnabled: boolean;
  sessionInitialized: boolean;
  isUserListening: boolean; // True when mic is on and expecting user speech
  isAssistantThinking: boolean; // True when waiting for assistant response
  currentTurnId: string | null; // Track current conversation turn
  // Audio processing related state will be managed by refs primarily
}

// System prompt from main.js (ensure this is the full prompt)
const DEFAULT_SYSTEM_PROMPT = `# Identity
- You are a **Worksite Safety Reporting Voice Agent**.
- You converse in **natural, friendly English** and efficiently help users report **observations or near misses**, or manage existing safety tickets.
- Always be clear, confirm information, and show active listening.

---

## Task
Act as a professional, safety-focused **voice assistant** that helps workers report **safety observations or near misses** via phone, or check/update existing reports. These reports may concern equipment, behavior, or hazardous conditions.

---

## Context Information
- The USER is calling to report an **observation** or a **near miss**, or to **check/update an existing ticket**.
- Caller ID is used to identify the user by name.

### For New Reports:
- Reports must include:
  - **Type** of report: observation or near miss
  - **Location** of the event
  - **Description** of what was observed
  - **Reporter's Name** (who is making the report)
  - **Reporter's Email**

### For Existing Tickets (Check/Update):
- If the USER wants to **check the status** or **update details** of a previously created ticket, you MUST ask for the **Ticket Number**.
- The USER should be asked to **spell the Ticket Number letter by letter** (e.g., \"A B C 1 2 3\").
- You MUST **confirm the spelled-out Ticket Number** with the USER before proceeding (e.g., \"Just to confirm, the ticket number is A B C 1 2 3, is that correct?\").
- For **updates**, you will ask what information needs to be changed (description, reporter name, or email) and confirm the changes.

---

## Model Instructions
- Ask if the USER wants to **create a new report** or **manage an existing ticket**.

### If Creating a New Report:
- Prompt the USER to choose: **observation** or **near miss**.
- Confirm each field (location, description, reporter's name, reporter's email) before submitting.
- After the USER describes the event and provides their details, **restate the full report back** to confirm.

### If Managing an Existing Ticket:
- Ask if they want to **check status** or **update a ticket**.
- Follow the **Ticket Number collection and confirmation process** described above.
- For **checking status**, provide the ticket details (description, reporter, status, creation date).
- For **updating a ticket**, ask which fields they want to update (description, reporter name, email). Confirm the new information and then confirm the update was made.

### General Instructions:
- Be conversational, precise, and efficient.
- **Do not ask unrelated questions** or offer unrelated services.
- Speak clearly and slowly when confirming names, locations, or ticket numbers.
- If something is unclear, ask once more, then offer to **restart the question** or that part of the process.

---

## Tool Use
- You have access to  \`createSupportTicketTool\`, \`getSupportTicketTool\`, and \`updateSupportTicketTool\`.
- Use \`createSupportTicketTool\` when the user wants to create a new safety report. It requires \`issueDescription\`, \`senderName\`, and \`senderEmail\`.
- Use \`getSupportTicketTool\` when the user wants to check the status or details of an existing ticket. It requires the \`ticketNumber\` (which you must obtain by asking the user to spell it out and then confirm).
- Use \`updateSupportTicketTool\` when the user wants to update an existing ticket. It requires the \`ticketNumber\` (obtained and confirmed as above) and can optionally take \`issueDescription\`, \`senderName\`, or \`senderEmail\` for the fields to be updated.
- When a ticket operation is successful, inform the user (e.g., \"Your report has been submitted, and your ticket number is XXXXX\", \"I've found ticket XXXXX\", \"Ticket XXXXX has been updated.\").
- If a ticket is not found (e.g., with \`getSupportTicketTool\` or \`updateSupportTicketTool\`), inform the user clearly, e.g., \"I couldn't find a ticket with the number you provided. Would you like to try another number or create a new report?\"

---

## Response Style and Format Requirements
- Use **natural, conversational** speech suitable for voice output.
- Confirm details clearly by **rephrasing** and repeating back.
- Keep a **calm, neutral, and supportive** tone.
- Use **simple, short sentences** for clarity and good ASR performance.
- Only ask one question at a time.
- Close the call warmly, e.g., \"Thanks for helping us keep the site safe!\" or \"Is there anything else I can help you with today?\"

---

## Example Dialogue (New Report)

**AI:**
"Hi Gary. Are you looking to create a new safety report or manage an existing one today?"

**USER:**
"Create a new one."

**AI:**
"Okay. Is it an observation or a near miss?"

**USER:**
"Observation."

**AI:**
"Got it. Where did the observation take place?"

**USER:**
"North maintenance shop at Long Canyon."

**AI:**
"Thanks. And what did you observe?"

**USER:**
"I saw a G2 contractor driving without their seatbelt."

**AI:**
"Alright. And what is your name?"

**USER:**
"It's Gary Smith."

**AI:**
"Thanks, Gary. And your email address?"

**USER:**
"gary.smith@example.com."

**AI:**
"Okay, Gary. So, just to confirm: you're reporting an observation of a G2 contractor driving without a seatbelt at the North maintenance shop at Long Canyon. The report is being made by Gary Smith, email gary.smith@example.com. Is that all correct?"

**USER:**
"Yes, that's right."

**AI:**
"Great. I've submitted your report. Your ticket number is [e.g., D1D9F]. Is there anything else I can help you with today?"

**USER:**
"No, that's all."

**AI:**
"Alright, thanks for your report, Gary. Take care."

## Example Dialogue (Check Ticket)

**AI:**
"Hi Sarah. Are you looking to create a new safety report or manage an existing one today?"

**USER:**
"I want to check on a ticket I created."

**AI:**
"Okay, I can help with that. Could you please spell out the ticket number for me, letter by letter?"

**USER:**
"Yes, it's D 1 D 9 F."

**AI:**
"Got it. So that's D, as in David, 1, D, as in David, 9, F, as in Frank. Is that correct?"

**USER:**
"Yes."

**AI:**
"Thanks. Let me look that up for you using \`getSupportTicketTool\`... (Tool is called) ... Okay, I found ticket D1D9F. It was an observation reported by Chris regarding 'descripcion prueba 3' and its current status is PENDING. Is there anything else?"

**USER:**
"No, that's it."

**AI:**
"Thanks for calling. Have a great day!"
`;

/**
 * Gets the chatbot ID from URL parameters with fallback to default
 * @returns {string} The chatbot ID to use
 */
const getChatbotIdFromUrl = (): string => {
  const DEFAULT_CHATBOT_ID = '084e0206-ea8c-45d4-aa47-36ee29900000';

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const chatbotIdFromUrl = urlParams.get('chatbotId');

    if (chatbotIdFromUrl && chatbotIdFromUrl.trim()) {
      logger.info(`🔧 Using chatbotId from URL: ${chatbotIdFromUrl}`);
      return chatbotIdFromUrl.trim();
    } else {
      logger.info(`🔧 No chatbotId in URL, using default: ${DEFAULT_CHATBOT_ID}`);
      return DEFAULT_CHATBOT_ID;
    }
  } catch (error) {
    logger.error('Error parsing URL parameters, using default chatbotId:', error);
    return DEFAULT_CHATBOT_ID;
  }
};

/**
 * Gets the channel ID from URL parameters
 * @returns {string | null} The channel ID from URL or null if not specified
 */
const getChannelIdFromUrl = (): string | null => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const channelIdFromUrl = urlParams.get('channelId');

    if (channelIdFromUrl && channelIdFromUrl.trim()) {
      logger.info(`🔧 Using channelId from URL: ${channelIdFromUrl}`);
      return channelIdFromUrl.trim();
    } else {
      logger.info(`🔧 No channelId in URL, will use sessionId as channelId`);
      return null;
    }
  } catch (error) {
    logger.error('Error parsing URL parameters for channelId:', error);
    return null;
  }
};

export interface VoiceAgentOptions {
  websocketUrl?: string;
  chatbotId?: string;
  channelId?: string | null;
  disabled?: boolean;
  onAssistantDataReceived?: (data: any) => void;
}

const useVoiceAgent = (options: VoiceAgentOptions = {}) => {
  const isDisabled = options.disabled === true;

  const [state, setState] = useState<VoiceAgentState>({
    connectionStatus: 'disconnected',
    isStreaming: false,
    chatMessages: [],
    isMicrophoneEnabled: false,
    sessionInitialized: false,
    isUserListening: false,
    isAssistantThinking: false,
    currentTurnId: null,
  });

  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const scriptProcessorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const audioPlayerRef = useRef<AudioPlayer | null>(null);
  const samplingRatioRef = useRef<number>(1);

  // To manage partial user messages
  const lastUserMessageContentRef = useRef<string>("");

  // Track current conversation turn for proper message concatenation
  const currentTurnIdRef = useRef<string | null>(null);

  // Add a debug counter for audio packets
  const audioPacketCounter = useRef<number>(0);

  const isStreamingRef = useRef(state.isStreaming);

  // Determine chatbot and channel identifiers
  const CHATBOT_ID = options.chatbotId || getChatbotIdFromUrl();
  const CHANNEL_ID =
    options.channelId !== undefined ? options.channelId : getChannelIdFromUrl();

  const buildConnectionUrl = useCallback(() => {
    const baseUrl = options.websocketUrl || config.urls.voiceAgentWebsocket;
    let connectionUrl = `${baseUrl}?chatbotId=${CHATBOT_ID}`;
    if (CHANNEL_ID) {
      connectionUrl += `&channelId=${CHANNEL_ID}`;
    }
    return connectionUrl;
  }, [options.websocketUrl, CHATBOT_ID, CHANNEL_ID]);

  // Keep isStreamingRef in sync with state.isStreaming
  useEffect(() => {
    logger.info("isStreaming changed to:", state.isStreaming);
    isStreamingRef.current = state.isStreaming;
  }, [state.isStreaming]);

  useEffect(() => {
    if (isDisabled) {
      logger.info("Voice agent disabled, skipping AudioPlayer setup");
      return;
    }

    logger.info("Initial setup of AudioPlayer");
    if (!audioPlayerRef.current) {
      audioPlayerRef.current = new AudioPlayer();
      // Start the audio player's context, but it doesn't play anything yet.
      audioPlayerRef.current.start().then(() => {
        logger.info("AudioPlayer.current.start() completed in initial setup.");
      });
    }

    return () => {
      logger.info("useVoiceAgent: Cleanup from initial AudioPlayer setup useEffect. AudioPlayer will NOT be closed here to persist its context.");
      // Persist audioPlayerRef.current and its AudioContext.
      // It will be explicitly closed if the main component unmounts or if disconnect is called.
      // audioPlayerRef.current?.close(); 
    };
  }, [isDisabled]);

  const addMessageToChat = useCallback((
    message: Omit<ChatMessage, 'id' | 'timestamp'>,
    isPartialUpdate: boolean = false,
    turnId?: string
  ) => {
    if (TEXT_DEBUG) {
      logger.info('📝 TEXT_MESSAGE: addMessageToChat called', {
        role: message.role,
        contentLength: message.content?.length,
        contentPreview: message.content?.substring(0, 50) + (message.content?.length > 50 ? '...' : ''),
        isPartialUpdate,
        latency: message.latency,
        turnId,
        currentTurnId: currentTurnIdRef.current
      });
    }

    setState(prevState => {
      let newMessages = [...prevState.chatMessages];

      // If this is a new turn, don't concatenate to previous messages
      if (turnId && turnId !== currentTurnIdRef.current) {
        currentTurnIdRef.current = turnId;
        if (TEXT_DEBUG) {
          logger.info('📝 TEXT_MESSAGE: New turn detected, forcing new message creation', {
            newTurnId: turnId,
            previousTurnId: currentTurnIdRef.current
          });
        }
        // Force new message creation for new turn
        isPartialUpdate = false;
      }

      // Si no hay mensajes, agregar el primero
      if (newMessages.length === 0) {
        const newId = Date.now().toString() + Math.random();
        if (TEXT_DEBUG) {
          logger.info('📝 TEXT_MESSAGE: Creating first message', { newId, role: message.role });
        }
        newMessages.push({
          ...message,
          id: newId,
          timestamp: new Date(),
          isPartial: isPartialUpdate,
          turnId: turnId || undefined, // Store the turnId
        });
        return { ...prevState, chatMessages: newMessages, currentTurnId: turnId || null };
      }

      // Obtener el último mensaje
      const lastMessage = newMessages[newMessages.length - 1];

      // Special case: if we're adding content to an existing placeholder message with the same turnId
      if (isPartialUpdate && message.content && turnId) {
        // Find existing message with same role and turnId (could be placeholder)
        const existingMessageIndex = newMessages.findIndex(msg =>
          msg.role === message.role &&
          msg.turnId === turnId &&
          msg.isPartial
        );

        if (existingMessageIndex !== -1) {
          const existingMessage = newMessages[existingMessageIndex];
          if (TEXT_DEBUG) {
            logger.info('📝 TEXT_MESSAGE: Updating existing placeholder/partial message', {
              messageId: existingMessage.id,
              oldContentLength: existingMessage.content.length,
              newContentLength: message.content.length,
              totalLength: existingMessage.content.length + message.content.length,
              turnId
            });
          }
          // Update the existing message
          newMessages[existingMessageIndex] = {
            ...existingMessage,
            content: existingMessage.content + ' ' + message.content,
            isPartial: true,
            latency: existingMessage.latency || message.latency,
          };
          return { ...prevState, chatMessages: newMessages, currentTurnId: turnId || prevState.currentTurnId };
        }
      }

      // Only concatenate if:
      // 1. It's a partial update
      // 2. Same role
      // 3. Same turn (not a new conversation flow)
      // 4. Last message is also partial
      // 5. Message has content to append
      // 6. Same turnId (now only same-turn chunks merge)
      if (isPartialUpdate &&
        lastMessage.role === message.role &&
        message.content &&
        lastMessage.isPartial &&
        lastMessage.turnId === turnId) {
        if (TEXT_DEBUG) {
          logger.info('📝 TEXT_MESSAGE: Appending to last message (fallback)', {
            messageId: lastMessage.id,
            oldContentLength: lastMessage.content.length,
            newContentLength: message.content.length,
            totalLength: lastMessage.content.length + message.content.length,
            turnId
          });
        }
        // Actualizar el último mensaje concatenando el contenido
        newMessages[newMessages.length - 1] = {
          ...lastMessage,
          content: lastMessage.content + message.content,
          isPartial: true,
          // Preservar la latencia original si existe
          latency: lastMessage.latency || message.latency,
        };
      }
      // Si es finalización (content vacío) y hay un mensaje del mismo rol y turnId
      else if (!isPartialUpdate && message.content === '' && turnId) {
        // Find the message with same role and turnId to finalize
        const messageToFinalizeIndex = newMessages.findIndex(msg =>
          msg.role === message.role &&
          msg.turnId === turnId &&
          msg.isPartial
        );

        if (messageToFinalizeIndex !== -1) {
          if (TEXT_DEBUG) {
            logger.info('📝 TEXT_MESSAGE: Finalizing existing message by turnId', {
              messageId: newMessages[messageToFinalizeIndex].id,
              finalContentLength: newMessages[messageToFinalizeIndex].content.length,
              turnId
            });
          }
          // Solo marcar como no parcial
          newMessages[messageToFinalizeIndex] = {
            ...newMessages[messageToFinalizeIndex],
            isPartial: false,
          };
        } else if (TEXT_DEBUG) {
          logger.info('📝 TEXT_MESSAGE: No message found to finalize with turnId:', turnId);
        }
      }
      // En cualquier otro caso, crear un nuevo mensaje
      else {
        const newId = Date.now().toString() + Math.random();
        if (TEXT_DEBUG) {
          logger.info('📝 TEXT_MESSAGE: Creating new message', {
            newId,
            role: message.role,
            reason: isPartialUpdate ? 'different role, new turn, or empty content' : 'new complete message',
            turnId
          });
        }
        newMessages.push({
          ...message,
          id: newId,
          timestamp: new Date(),
          isPartial: isPartialUpdate,
          turnId: turnId || undefined, // Store the turnId
        });
      }

      if (TEXT_DEBUG) {
        logger.info('📝 TEXT_MESSAGE: Total messages after update:', newMessages.length);
      }
      return { ...prevState, chatMessages: newMessages, currentTurnId: turnId || prevState.currentTurnId };
    });
  }, []);

  // Simplified system message deduplication only (not for voice chunks)
  const addSystemMessage = useCallback((content: string) => {
    const now = Date.now();
    const recentThreshold = 2000; // 2 seconds

    // Only check for duplicate SYSTEM messages
    const isDuplicate = state.chatMessages.some(msg =>
      msg.role === 'SYSTEM' &&
      msg.content === content &&
      (now - new Date(msg.timestamp).getTime()) < recentThreshold
    );

    if (!isDuplicate) {
      addMessageToChat({ role: 'SYSTEM', content }, false);
    }
  }, [state.chatMessages, addMessageToChat]);

  // Latency tracking
  const userTextReceivedTimeRef = useRef<number | null>(null);
  const firstAssistantResponseRef = useRef<boolean>(false);

  // Reset latency tracking
  const resetLatencyTracking = useCallback(() => {
    userTextReceivedTimeRef.current = null;
    firstAssistantResponseRef.current = false;
    logger.info("Latency tracking reset");
  }, []);

  // End turn management
  const endTurn = useCallback(() => {
    // Reset turn ID to force new messages for next interaction
    currentTurnIdRef.current = null;

    if (TEXT_DEBUG) {
      logger.info('🔄 TURN_END: Ending conversation turn, resetting turn tracking');
    }

    // Mark all messages as final (not partial)
    setState(prevState => ({
      ...prevState,
      currentTurnId: null,
      chatMessages: prevState.chatMessages.map(msg => ({
        ...msg,
        isPartial: false
      }))
    }));
  }, []);

  // Define critical event handlers outside useEffect to ensure they're available early
  const handleServerReady = useCallback((data: any) => {
    logger.info('🎯 CLIENT: handleServerReady function CALLED!', data);
    logger.info('🎯 CLIENT: Received serverReady from server:', data);
    logger.info('🎯 CLIENT: Current connection status:', webSocketService.getConnectionStatus());

    // NOW send startCallFlow since server is ready to receive events
    logger.info('🎯 CLIENT: Server is ready! Sending startCallFlow...');
    if (CHATBOT_ID === '084e0206-ea8c-45d4-aa47-36ee29900000') {
      logger.info('🎯 CLIENT: CHATBOT_ID is 084e0206-ea8c-45d4-aa47-36ee29900000, sending startCallFlow with systemPrompt');
      webSocketService.emitEvent('startCallFlow', {
        systemPrompt: DEFAULT_SYSTEM_PROMPT,
        chatbotId: CHATBOT_ID
      });
    } else {
      webSocketService.emitEvent('startCallFlow', {
        systemPrompt: undefined,
        chatbotId: CHATBOT_ID
      });
    }

    addSystemMessage(`Server ready: ${data.message || ''}. Initializing session...`);
  }, [addSystemMessage, CHATBOT_ID]);

  logger.info("🔧 DEBUG: handleServerReady function defined OUTSIDE useEffect:", typeof handleServerReady);

  // Add ref to track whether to display assistant text (for SPECULATIVE vs FINAL handling)
  const displayAssistantTextRef = useRef<boolean>(false);

  // WebSocket Event Handlers and Initialization
  useEffect(() => {
    if (isDisabled) {
      logger.info("useVoiceAgent: Voice agent disabled, skipping WebSocket setup");
      return;
    }

    logger.info("useVoiceAgent: Setting up WebSocket event handlers BEFORE any connection attempts.");

    const connectionUrl = buildConnectionUrl();
    logger.info("🔧 DEBUG: Will use connection URL:", connectionUrl);

    // Initialize webSocketService to create socket before registering handlers
    // This ensures socket exists when we register handlers, but doesn't start connection
    // Guard against multiple socket creation
    if (!webSocketService.isInitialized()) {
      logger.info("🔧 DEBUG: Creating WebSocket socket without connecting...");
      webSocketService.createSocketOnly(connectionUrl);
    } else {
      logger.info("🔧 DEBUG: WebSocket already initialized, skipping socket creation");
    }

    const handleStatusChange = (status: WebSocketConnectionStatus) => {
      setState(prevState => {
        const newState = { ...prevState, connectionStatus: status };

        if (status === 'connected' && !prevState.sessionInitialized) {
          logger.info('🔌 CLIENT: Connected. Waiting for serverReady before sending startCallFlow...');

          // DON'T send startCallFlow here - wait for serverReady event
          return {
            ...newState,
            sessionInitialized: false,  // ✅ Wait for server confirmation
            isUserListening: false,
            isAssistantThinking: false
          };
        } else if (status === 'disconnected') {
          logger.info("useVoiceAgent: WebSocket disconnected. Updating state (using functional setState).");
          return {
            ...newState,
            isStreaming: false,
            sessionInitialized: false,
            isUserListening: false,
            isAssistantThinking: false
          };
        } else if (status === 'error') {
          logger.error("useVoiceAgent: WebSocket connection error. Updating state (using functional setState).");
          return {
            ...newState,
            isStreaming: false,
            sessionInitialized: false,
            isUserListening: false,
            isAssistantThinking: false
          };
        }
        return newState; // Default: just return the updated connectionStatus
      });

      // Handle chat messages after the state update, based on the status and flags
      if (status === 'connected') {
        addSystemMessage('Connected to server. Waiting for server ready signal...');
      }
      if (status === 'disconnected') {
        addSystemMessage('Disconnected from server.');
      } else if (status === 'error') {
        addMessageToChat({ role: 'ERROR', content: 'Connection error.' });
      }
    };

    const handleTextOutput = (data: { role?: 'USER' | 'ASSISTANT'; content: string; type?: string; source?: string; latency?: number; contentId?: string }) => {
      if (TEXT_DEBUG) {
        logger.info('🔤 TEXT_EVENT: handleTextOutput called', {
          role: data.role,
          type: data.type,
          contentLength: data.content?.length,
          contentPreview: data.content?.substring(0, 100) + (data.content?.length > 100 ? '...' : ''),
          latency: data.latency,
          contentId: data.contentId,
          displayAssistantText: data.role === 'ASSISTANT' ? displayAssistantTextRef.current : 'N/A'
        });
      }

      // NO verificar duplicados para textOutput ya que son chunks parciales
      // La deduplicación solo debe aplicarse a mensajes completos del sistema

      const role: 'USER' | 'ASSISTANT' = data.role || (data.type === 'asr' ? 'USER' : 'ASSISTANT'); // Fallback logic
      const turnId = data.contentId || currentTurnIdRef.current || undefined;

      if (role === 'USER') {
        if (userTextReceivedTimeRef.current === null) { // Should be set by contentStart typically
          userTextReceivedTimeRef.current = Date.now();
          firstAssistantResponseRef.current = false; // Reset for assistant's response
          if (TEXT_DEBUG) {
            logger.info(`🔤 TEXT_EVENT: User text received (first chunk) at: ${userTextReceivedTimeRef.current}`);
          }
        }
        // Immediately hide listening indicator when user text arrives
        setState(prevState => ({
          ...prevState,
          isUserListening: false,
          isAssistantThinking: false // Not thinking yet until user finishes
        }));
        lastUserMessageContentRef.current += data.content; // Still useful for other potential logic

        // Add user message with proper turn tracking
        if (TEXT_DEBUG) {
          logger.info('🔤 TEXT_EVENT: Adding USER message chunk to chat with turnId:', turnId);
        }
        addMessageToChat({ role: role, content: data.content }, true, turnId);
      } else if (role === 'ASSISTANT') {
        // Process assistant messages based on their type (speculative vs final)
        const shouldDisplay = data.type === 'speculative' || data.type === 'asr' || !data.type; // Display speculative, asr, or unknown types

        // Only show assistant message if it's not FINAL duplicate
        if (shouldDisplay && displayAssistantTextRef.current) {
          let latency = data.latency;
          if (!firstAssistantResponseRef.current && userTextReceivedTimeRef.current !== null) {
            latency = Date.now() - userTextReceivedTimeRef.current;
            firstAssistantResponseRef.current = true;
            if (TEXT_DEBUG) {
              logger.info(`🔤 TEXT_EVENT: Response latency: ${latency}ms (from last user text to first assistant response)`);
            }
          }
          // Hide thinking indicator when assistant starts responding
          setState(prevState => ({
            ...prevState,
            isAssistantThinking: false
          }));

          // Add assistant message
          if (TEXT_DEBUG) {
            logger.info('🔤 TEXT_EVENT: Adding ASSISTANT message to chat', {
              type: data.type,
              contentPreview: data.content?.substring(0, 100) + (data.content?.length > 100 ? '...' : ''),
              latency,
              turnId
            });
          }
          addMessageToChat({ role: role, content: data.content, latency }, true, turnId);
        } else if (TEXT_DEBUG) {
          logger.info('🔤 TEXT_EVENT: Ignoring ASSISTANT message (FINAL content or not displayable)', {
            type: data.type,
            shouldDisplay,
            displayAssistantText: displayAssistantTextRef.current,
            contentPreview: data.content?.substring(0, 100) + (data.content?.length > 100 ? '...' : '')
          });
        }
      }
    };

    const handleAudioOutput = (data: { content: string }) => {
      if (AUDIO_DEBUG) {
        logger.info('🎵 AUDIO_EVENT: handleAudioOutput called', {
          contentLength: data?.content?.length
        });
      }

      if (data.content && audioPlayerRef.current) {
        setState(prevState => ({ ...prevState, isAssistantThinking: false })); // Assistant responding with audio
        try {
          if (AUDIO_DEBUG) {
            logger.info('🎵 AUDIO_EVENT: Converting base64 to Float32Array...');
          }
          const float32Audio = base64ToFloat32Array(data.content);
          if (AUDIO_DEBUG) {
            logger.info('🎵 AUDIO_EVENT: Converted audio length:', float32Audio.length);
            logger.info('🎵 AUDIO_EVENT: Sample values (first 5):', Array.from(float32Audio.slice(0, 5)));
            logger.info('🎵 AUDIO_EVENT: Max amplitude:', Math.max(...float32Audio));
            logger.info('🎵 AUDIO_EVENT: Min amplitude:', Math.min(...float32Audio));
            logger.info('🎵 AUDIO_EVENT: Calling audioPlayer.playAudio...');
          }

          audioPlayerRef.current.playAudio(float32Audio);
          if (AUDIO_DEBUG) {
            logger.info('🎵 AUDIO_EVENT: playAudio call completed');
          }

          // Don't create a separate chat message for audio - the text output handler will handle the message
          // This prevents duplicate messages when both text and audio are received
          if (AUDIO_DEBUG) {
            logger.info('🎵 AUDIO_EVENT: NOT creating chat message - audio only handles playback');
          }
        } catch (error) {
          logger.error("🎵 AUDIO_EVENT: Error processing or playing received audio:", error);
          if (TEXT_DEBUG) {
            logger.info('📝 TEXT_MESSAGE: Adding ERROR message to chat (from audio error)');
          }
          addMessageToChat({ role: 'ERROR', content: 'Failed to play received audio.' });
        }
      } else if (AUDIO_DEBUG) {
        logger.warn('🎵 AUDIO_EVENT: Missing data content or audioPlayerRef:', {
          hasContent: !!data.content,
          hasAudioPlayer: !!audioPlayerRef.current
        });
      }
    };

    const handleContentStart = (data: { type: 'TEXT' | 'AUDIO', role?: 'USER' | 'ASSISTANT', additionalModelFields?: string, contentId?: string }) => {
      if (TEXT_DEBUG && data.type === 'TEXT') {
        logger.info('🚀 CONTENT_START: TEXT Event received', data);
      } else if (AUDIO_DEBUG && data.type === 'AUDIO') {
        logger.info('🚀 CONTENT_START: AUDIO Event received', data);
      }

      if (data.type === 'TEXT' && data.role) {
        // Use contentId as turn identifier
        const turnId = data.contentId || Date.now().toString();

        if (data.role === 'USER') {
          // New user message starts - this should appear BEFORE assistant response
          currentTurnIdRef.current = turnId;
          if (TEXT_DEBUG) {
            logger.info('🚀 CONTENT_START: Starting new USER content with turnId:', turnId);
          }
          lastUserMessageContentRef.current = ""; // Reset accumulator for new user message
          resetLatencyTracking(); // Reset latency tracking for the new user message
          userTextReceivedTimeRef.current = Date.now(); // User message starts now
          firstAssistantResponseRef.current = false;

          // Check if we should replace an existing empty placeholder with new turnId
          setState(prevState => {
            const lastMessage = prevState.chatMessages[prevState.chatMessages.length - 1];
            const isLastMessageEmptyPlaceholder = lastMessage &&
              lastMessage.role === 'USER' &&
              lastMessage.content.trim() === '' &&
              lastMessage.isPartial === true;

            let newMessages = [...prevState.chatMessages];

            if (isLastMessageEmptyPlaceholder) {
              if (TEXT_DEBUG) {
                logger.info('🚀 CONTENT_START: Removing previous empty USER placeholder and creating new one with updated turnId:', turnId);
              }
              // Remove the last empty placeholder
              newMessages.pop();
            }

            // Create new placeholder with current turnId
            const newId = Date.now().toString() + Math.random();
            newMessages.push({
              role: 'USER',
              content: '',
              id: newId,
              timestamp: new Date(),
              isPartial: true,
              turnId: turnId
            });

            if (TEXT_DEBUG) {
              logger.info('🚀 CONTENT_START: Created placeholder USER message with turnId:', turnId);
            }

            return { ...prevState, chatMessages: newMessages };
          });

          // Hide user listening, show that user is speaking
          setState(prevState => ({
            ...prevState,
            isUserListening: false,
            isAssistantThinking: false // User is speaking, not assistant thinking yet
          }));
        } else if (data.role === 'ASSISTANT') {
          // Handle SPECULATIVE vs FINAL generation stages like original main.js
          let isSpeculative = false;
          let shouldDisplayText = false;

          try {
            if (data.additionalModelFields) {
              const additionalFields = JSON.parse(data.additionalModelFields);
              isSpeculative = additionalFields.generationStage === "SPECULATIVE";

              if (isSpeculative) {
                if (TEXT_DEBUG) {
                  logger.info('🚀 CONTENT_START: Received SPECULATIVE content - will display');
                }
                shouldDisplayText = true;
              } else {
                if (TEXT_DEBUG) {
                  logger.info('🚀 CONTENT_START: Received FINAL content - will NOT display (ignoring duplicate)');
                }
                shouldDisplayText = false;
              }
            } else {
              // Fallback: if no additionalModelFields, display the content
              if (TEXT_DEBUG) {
                logger.info('🚀 CONTENT_START: No additionalModelFields found, defaulting to display');
              }
              shouldDisplayText = true;
            }
          } catch (e) {
            logger.error('🚀 CONTENT_START: Error parsing additionalModelFields:', e);
            // Fallback: display the content
            shouldDisplayText = true;
          }

          displayAssistantTextRef.current = shouldDisplayText;

          // If assistant starts speaking, it's no longer "thinking" in the sense of waiting for input.
          setState(prevState => ({ ...prevState, isAssistantThinking: false }));
        }
      }
      // Note: Audio contentStart might have different implications if we were to display audio indicators differently.
    };

    const handleContentEnd = (data: { type: 'TEXT' | 'AUDIO', role?: 'USER' | 'ASSISTANT', stopReason?: string, additionalModelFields?: string, contentId?: string }) => {
      if (TEXT_DEBUG && data.type === 'TEXT') {
        logger.info('🏁 CONTENT_END: TEXT Event received', data);
      } else if (AUDIO_DEBUG && data.type === 'AUDIO') {
        logger.info('🏁 CONTENT_END: AUDIO Event received', data);
      }

      if (data.type === 'TEXT' && data.role) {
        // Use contentId as turnId for proper message mapping
        const turnId = data.contentId || currentTurnIdRef.current || undefined;

        if (data.role === 'USER') {
          // User finished speaking, now assistant should think
          if (TEXT_DEBUG) {
            logger.info('🏁 CONTENT_END: USER content ended - finalizing transcript with turnId:', turnId);
          }
          addMessageToChat({ role: data.role, content: '', turnId }, false, turnId);
          setState(prevState => ({
            ...prevState,
            isUserListening: false,
            isAssistantThinking: true // NOW assistant is thinking
          }));
        } else if (data.role === 'ASSISTANT') {
          // Always finalize ASSISTANT message to close the bubble (regardless of shouldProcessContentEnd)
          if (TEXT_DEBUG) {
            logger.info('🏁 CONTENT_END: Finalizing ASSISTANT TEXT message (always) with turnId:', turnId);
          }
          addMessageToChat(
            { role: 'ASSISTANT', content: '', turnId }, // empty → do not change text
            false,                              // not partial → marks as complete
            turnId
          );
          setState(prevState => ({
            ...prevState,
            isAssistantThinking: false
          }));
          resetLatencyTracking(); // Reset after assistant fully sends its message
        }
      }

      if (data.type === 'AUDIO' && data.role === 'ASSISTANT') {
        // If assistant audio ends, it's no longer "thinking"
        if (AUDIO_DEBUG) {
          logger.info('🏁 CONTENT_END: AUDIO ended for ASSISTANT');
        }
        setState(prevState => ({ ...prevState, isAssistantThinking: false }));
      }

      if (data.stopReason?.toUpperCase() === 'INTERRUPTED') {
        if (TEXT_DEBUG || AUDIO_DEBUG) {
          logger.info('🏁 CONTENT_END: INTERRUPTED - calling bargeIn');
        }
        audioPlayerRef.current?.bargeIn();
      }
    };

    const handleStreamComplete = () => {
      logger.info('Received streamComplete from server.');
      setState(prevState => ({
        ...prevState,
        isStreaming: false,
        // Don't reset sessionInitialized here - keep session active for new conversations
        // sessionInitialized: false,
        isUserListening: false,
        isAssistantThinking: false,
      }));
      addSystemMessage('Conversation complete. Ready for new conversation.');

      // End the current turn and finalize all messages
      endTurn();

      // Reset latency tracking
      userTextReceivedTimeRef.current = null;
      firstAssistantResponseRef.current = false;

      // Don't automatically disconnect - let user control this
      // webSocketService.disconnect();
    };

    const handleErrorEvent = (error: any) => {
      logger.error('Received error event from server:', error);
      addMessageToChat({ role: 'ERROR', content: `Server error: ${error.message || JSON.stringify(error)}` });
      setState(prevState => ({ ...prevState, isStreaming: false, isUserListening: false, isAssistantThinking: false }));
    };

    // Add handler for the new callFlowStarted event
    const handleCallFlowStarted = (data: any) => {
      logger.info('🎯 CLIENT: Received callFlowStarted from server:', data);

      // NOW set sessionInitialized = true since server confirmed
      setState(prevState => ({
        ...prevState,
        sessionInitialized: true,
        isUserListening: false,
        isAssistantThinking: false
      }));

      addSystemMessage(`✅ Session initialized! ${data.message || ''} Ready to start streaming audio.`);

      // CRITICAL: Auto-start audio streaming immediately after session is initialized
      // This prevents the timeout issue where the server is waiting for audio input
      logger.info('🎯 CLIENT: Auto-starting audio streaming after session initialization...');
      //setTimeout(() => {
      //  startStreamingAudio();
      //}, 100); // Small delay to ensure state update is processed
    };

    // Handle sentiment/analysis events
    const handleSentiment = (data: any) => {
      logger.info('🧠 CLIENT: Received sentiment/analysis data:', data);
      if (options.onAssistantDataReceived) {
        options.onAssistantDataReceived(data);
      }
    };

    logger.info("useVoiceAgent: Registering WebSocket event handlers (textOutput, audioOutput, sentiment, etc.).");
    logger.info("🔧 DEBUG: WebSocket connection status:", webSocketService.getConnectionStatus());

    webSocketService.onConnectionStatusChange(handleStatusChange);
    webSocketService.onEvent('textOutput', handleTextOutput);
    webSocketService.onEvent('audioOutput', handleAudioOutput);
    webSocketService.onEvent('contentStart', handleContentStart);
    webSocketService.onEvent('contentEnd', handleContentEnd);
    webSocketService.onEvent('streamComplete', handleStreamComplete);
    webSocketService.onEvent('callFlowStarted', handleCallFlowStarted);
    webSocketService.onEvent('sentiment', handleSentiment);
    logger.info("🔧 DEBUG: About to register serverReady handler:", typeof handleServerReady);
    logger.info("🔧 DEBUG: WebSocket status before serverReady registration:", webSocketService.getConnectionStatus());
    webSocketService.onEvent('serverReady', handleServerReady);
    logger.info("🔧 DEBUG: serverReady handler registered successfully");
    webSocketService.onEvent('error', handleErrorEvent);

    return () => {
      logger.info("useVoiceAgent: Cleaning up WebSocket event handlers and status change callback.");
      webSocketService.onConnectionStatusChange(() => { }); // Clear callback
      webSocketService.offEvent('textOutput', handleTextOutput);
      webSocketService.offEvent('audioOutput', handleAudioOutput);
      webSocketService.offEvent('contentStart', handleContentStart);
      webSocketService.offEvent('contentEnd', handleContentEnd);
      webSocketService.offEvent('streamComplete', handleStreamComplete);
      webSocketService.offEvent('callFlowStarted', handleCallFlowStarted);
      webSocketService.offEvent('sentiment', handleSentiment);
      webSocketService.offEvent('serverReady', handleServerReady);
      webSocketService.offEvent('error', handleErrorEvent);
      // Do not disconnect the socket here, that's handled by the top-level unmount effect or explicit disconnect call
    };
    // Re-run this setup when connection parameters change so new sockets get the handlers
  }, [addMessageToChat, handleServerReady, CHATBOT_ID, CHANNEL_ID, options.websocketUrl, isDisabled]); // Effect for WebSocket setup and event handling

  const requestMicrophoneAccess = useCallback(async () => {
    if (isDisabled) {
      logger.info("Voice agent disabled, microphone access denied");
      return false;
    }

    // If mic is already enabled and we have a valid stream, just return true
    if (state.isMicrophoneEnabled && mediaStreamRef.current && audioContextRef.current) {
      logger.info("Microphone already enabled, reusing existing context");
      return true;
    }

    // Clean up existing resources if needed
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }

    try {
      addMessageToChat({ role: 'SYSTEM', content: "Requesting microphone access..." });
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      mediaStreamRef.current = stream;

      if (isFirefox()) {
        audioContextRef.current = new AudioContext();
      } else {
        audioContextRef.current = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
      }
      samplingRatioRef.current = audioContextRef.current.sampleRate / TARGET_SAMPLE_RATE;

      await audioPlayerRef.current?.start();

      setState(prevState => ({ ...prevState, isMicrophoneEnabled: true }));
      addMessageToChat({ role: 'SYSTEM', content: "Microphone access granted. Ready." });
      return true;
    } catch (error) {
      logger.error("Error accessing microphone:", error);
      setState(prevState => ({ ...prevState, isMicrophoneEnabled: false }));
      addMessageToChat({ role: 'ERROR', content: `Microphone access denied: ${error}` });
      return false;
    }
  }, [addMessageToChat, state.isMicrophoneEnabled, isDisabled]);

  const sendAudioData = useCallback((audioData: string, isCurrentlyStreaming: boolean) => {
    if (webSocketService.getConnectionStatus() === 'connected' && isCurrentlyStreaming) {
      webSocketService.emitEvent('audioInput', audioData);
      audioPacketCounter.current++;

      // Reduce audio packet logging significantly
      if (AUDIO_PACKET_DEBUG && audioPacketCounter.current % 50 === 0) {
        logger.info(`📦 AUDIO_PACKET: Sent ${audioPacketCounter.current} audio packets`);
      }
    } else if (AUDIO_DEBUG) {
      logger.warn(`sendAudioData: Not sending. connectionStatus: ${webSocketService.getConnectionStatus()}, isCurrentlyStreamingArg: ${isCurrentlyStreaming}. Packet counter remains ${audioPacketCounter.current}`);
    }
  }, []); // No dependencies, uses live data or args

  const startStreamingAudio = useCallback(async () => {
    if (isDisabled) {
      logger.info("Voice agent disabled, cannot start streaming audio");
      return;
    }

    logger.info("startStreamingAudio: Called. Current state:", {
      isMicrophoneEnabled: state.isMicrophoneEnabled,
      connectionStatus: state.connectionStatus,
      sessionInitialized: state.sessionInitialized,
      isStreaming: state.isStreaming,
      audioContextState: audioContextRef.current?.state
    });

    if (state.isStreaming) {
      logger.warn("startStreamingAudio: Already streaming. Exiting.");
      return;
    }

    // 1. Ensure WebSocket is connected.
    if (state.connectionStatus !== 'connected') {
      logger.info("startStreamingAudio: WebSocket not connected. Attempting to connect.");
      addMessageToChat({ role: 'SYSTEM', content: "Connecting to server before streaming..." });
      webSocketService.connect(); // connect() should initialize if needed and handle its own status updates.
      // We expect handleStatusChange to eventually send startCallFlow if this is the first connect.
      return;
    }

    // 2. Ensure session with server is initialized (wait for callFlowStarted response)
    if (!state.sessionInitialized) {
      logger.warn("startStreamingAudio: Session not initialized. Client should wait for callFlowStarted event.");
      addMessageToChat({ role: 'ERROR', content: "⚠️ Session not ready. Please wait for initialization to complete before streaming audio." });
      return; // Don't proceed until session is confirmed ready
    }

    // 3. Acquire and set up microphone and audio context.
    if (mediaStreamRef.current) {
      logger.info("startStreamingAudio: Stopping existing media tracks before requesting new ones.");
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    try {
      logger.info("startStreamingAudio: Requesting fresh microphone stream.");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      mediaStreamRef.current = stream;
      logger.info(`startStreamingAudio: Microphone stream acquired. Active: ${stream.active}, Tracks: ${stream.getAudioTracks().length}`);
      setState(prevState => ({ ...prevState, isMicrophoneEnabled: true }));
    } catch (error) {
      logger.error("startStreamingAudio: Error getting microphone access:", error);
      setState(prevState => ({ ...prevState, isMicrophoneEnabled: false }));
      addMessageToChat({ role: 'ERROR', content: `Microphone access error: ${error}` });
      return;
    }

    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      logger.info("startStreamingAudio: AudioContext is closed or null, creating a new one.");
      audioContextRef.current = new AudioContext(isFirefox() ? undefined : { sampleRate: TARGET_SAMPLE_RATE });
      samplingRatioRef.current = audioContextRef.current.sampleRate / TARGET_SAMPLE_RATE;
    }
    if (audioContextRef.current.state === 'suspended') {
      logger.info("startStreamingAudio: Resuming suspended AudioContext.");
      await audioContextRef.current.resume();
    }
    logger.info("startStreamingAudio: AudioContext state is ready:", audioContextRef.current.state);

    // 4. Set up audio processing pipeline and start streaming state.
    setState(prevState => ({ ...prevState, isStreaming: true, isUserListening: true, isAssistantThinking: false }));
    isStreamingRef.current = true; // Update ref immediately
    addMessageToChat({ role: 'SYSTEM', content: "🎤 Audio streaming started. Microphone is active." });
    logger.info("startStreamingAudio: Set isStreaming=true. Setting up audio nodes.");

    try {
      if (scriptProcessorNodeRef.current) scriptProcessorNodeRef.current.disconnect();
      if (audioSourceNodeRef.current) audioSourceNodeRef.current.disconnect();
      audioPacketCounter.current = 0; // Reset packet counter

      audioSourceNodeRef.current = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
      scriptProcessorNodeRef.current = audioContextRef.current.createScriptProcessor(512, 1, 1);

      scriptProcessorNodeRef.current.onaudioprocess = (e) => {
        const currentStreamingStatus = isStreamingRef.current;
        // logger.info("onaudioprocess: Fired. Packet:", audioPacketCounter.current);
        if (!currentStreamingStatus) { // Check ref for immediate consistency
          // logger.warn("onaudioprocess: Not processing, isStreamingRef.current is false.");
          return;
        }
        // No need to check WebSocket connection status here, sendAudioData will handle it if socket drops.

        const inputData = e.inputBuffer.getChannelData(0);
        let hasSound = false;
        let maxAmplitude = 0;
        for (let i = 0; i < inputData.length; i++) {
          const absSample = Math.abs(inputData[i]);
          if (absSample > maxAmplitude) maxAmplitude = absSample;
          if (absSample > 0.005) hasSound = true; // Slightly lower threshold for hasSound detection
        }

        // Log first few packets and then periodically
        if (AUDIO_PACKET_DEBUG && (audioPacketCounter.current < 5 || audioPacketCounter.current % 100 === 0)) {
          logger.info(
            `📦 AUDIO_PACKET: P${audioPacketCounter.current}. ` +
            `Samples[0-2]: ${inputData[0]?.toFixed(3)},${inputData[1]?.toFixed(3)},${inputData[2]?.toFixed(3)}. ` +
            `Len:${inputData.length}. MaxAmp:${maxAmplitude.toFixed(3)}. Sound:${hasSound}. ` +
            `StreamRef:${currentStreamingStatus}.`
          );
        }

        // Always send the first ~N packets to ensure server receives *something* after audioStart
        // After that, optionally filter silence.
        const ALWAYS_SEND_INITIAL_PACKETS = 20; // Send first 20 packets regardless of silence

        if (audioPacketCounter.current >= ALWAYS_SEND_INITIAL_PACKETS && !hasSound) {
          // Optionally skip sending silent packets after the initial burst
          // if (AUDIO_PACKET_DEBUG && audioPacketCounter.current % 100 === 0) logger.info("📦 AUDIO_PACKET: Skipping silent packet", audioPacketCounter.current);
          // return; // UNCOMMENT TO ENABLE SILENCE SKIPPING
        }

        const pcmData = float32To16BitPCM(inputData, samplingRatioRef.current);
        const base64Data = arrayBufferToBase64(pcmData.buffer);
        sendAudioData(base64Data, currentStreamingStatus);
      };

      audioSourceNodeRef.current.connect(scriptProcessorNodeRef.current);
      scriptProcessorNodeRef.current.connect(audioContextRef.current.destination);

      logger.info("startStreamingAudio: Audio processing pipeline connected. Ready to send audio data.");

    } catch (error) {
      logger.error("startStreamingAudio: Error setting up audio processing pipeline:", error);
      addMessageToChat({ role: 'ERROR', content: `Audio setup error: ${error}` });
      // Rollback streaming state if setup failed
      setState(prevState => ({ ...prevState, isStreaming: false, isUserListening: false }));
      isStreamingRef.current = false;
    }
  }, [state.connectionStatus, state.isMicrophoneEnabled, state.sessionInitialized, state.isStreaming, addMessageToChat, sendAudioData, isDisabled]);

  const stopStreamingAudio = useCallback(() => {
    logger.info("stopStreamingAudio called. Current isStreaming state:", state.isStreaming);
    if (!state.isStreaming && !mediaStreamRef.current && !audioSourceNodeRef.current && !scriptProcessorNodeRef.current) {
      logger.warn("stopStreamingAudio: Already stopped or nothing to stop.");
      return;
    }

    // Set isStreaming to false immediately to prevent re-entry and update UI.
    // This should be the primary way to stop the onaudioprocess loop.
    setState(prevState => {
      if (!prevState.isStreaming) return prevState; // Avoid unnecessary state update if already false
      logger.info("stopStreamingAudio: Setting isStreaming to false.");
      isStreamingRef.current = false;
      return { ...prevState, isStreaming: false, isUserListening: false };
    });
    addMessageToChat({ role: 'SYSTEM', content: "Audio streaming stopped." });

    // Disconnect audio processing nodes
    if (scriptProcessorNodeRef.current) {
      logger.info("stopStreamingAudio: Disconnecting scriptProcessorNode.");
      scriptProcessorNodeRef.current.disconnect();
      scriptProcessorNodeRef.current.onaudioprocess = null; // Important to remove the handler
      scriptProcessorNodeRef.current = null;
    }
    if (audioSourceNodeRef.current) {
      logger.info("stopStreamingAudio: Disconnecting audioSourceNode.");
      audioSourceNodeRef.current.disconnect();
      audioSourceNodeRef.current = null;
    }

    // Stop media tracks
    if (mediaStreamRef.current) {
      logger.info("stopStreamingAudio: Stopping media stream tracks.");
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null; // Release the stream
    }

    // Inform the server that audio has stopped.
    if (webSocketService.getConnectionStatus() === 'connected') {
      logger.info("stopStreamingAudio: Emitting 'stopAudio' event to server.");
      webSocketService.emitEvent('stopAudio', null);
    } else {
      logger.warn("stopStreamingAudio: Not connected to server, cannot emit 'stopAudio'.");
    }

    // React state will automatically re-enable the start button when isStreaming becomes false
    logger.info("stopStreamingAudio: Operations complete. Start button should be re-enabled by React state.");
  }, [state.isStreaming, addMessageToChat]);

  const connect = useCallback(() => {
    if (isDisabled) {
      logger.info('Voice agent disabled, cannot connect');
      return;
    }

    logger.info('🔌 CONNECT CALLBACK: Called with state:', {
      connectionStatus: state.connectionStatus,
      CHATBOT_ID,
      CHANNEL_ID
    });

    if (state.connectionStatus === 'disconnected' || state.connectionStatus === 'error') {
      addMessageToChat({ role: 'SYSTEM', content: 'Connecting to server...' });

      const connectionUrl = buildConnectionUrl();
      logger.info('🔌 CONNECT CALLBACK: Connecting to:', connectionUrl);

      // Connect with the URL that includes chatbotId and channelId (this should NOT recreate the socket now)
      webSocketService.connect(connectionUrl);
    } else {
      logger.info('🔌 CONNECT CALLBACK: Not connecting, current status:', state.connectionStatus);
    }
  }, [state.connectionStatus, addMessageToChat, buildConnectionUrl, CHATBOT_ID, CHANNEL_ID, isDisabled]);

  const disconnect = useCallback(() => {
    // ... (same as before, with thinking flags reset) ...
    if (state.connectionStatus === 'connected') {
      addMessageToChat({ role: 'SYSTEM', content: 'Disconnecting...' });
      if (state.isStreaming) {
        stopStreamingAudio();
      }
      webSocketService.disconnect();
    }

    // We should keep the microphone access and AudioContext alive
    // for easier reconnection

    setState(prevState => ({
      ...prevState,
      sessionInitialized: false,
      isUserListening: false,
      isAssistantThinking: false
    }));
  }, [state.connectionStatus, state.isStreaming, addMessageToChat, stopStreamingAudio]);

  const clearChatMessages = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      chatMessages: [],
      currentTurnId: null,
    }));
    lastUserMessageContentRef.current = "";
    currentTurnIdRef.current = null;
  }, []);


  // Check if browser has microphone permission
  useEffect(() => {
    if (isDisabled) {
      logger.info("Voice agent disabled, skipping microphone permission check");
      return;
    }

    const checkMicrophonePermission = async () => {
      try {
        logger.info("Checking microphone permission status");
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });

        logger.info("Microphone permission status:", permissionStatus.state);

        if (permissionStatus.state === 'granted' && !state.isMicrophoneEnabled) {
          logger.info("Microphone permission already granted, setting up audio");
          requestMicrophoneAccess();
        }

        // Listen for permission changes
        permissionStatus.onchange = () => {
          logger.info("Microphone permission changed to:", permissionStatus.state);
          if (permissionStatus.state === 'granted' && !state.isMicrophoneEnabled) {
            requestMicrophoneAccess();
          } else if (permissionStatus.state !== 'granted') {
            setState(prevState => ({ ...prevState, isMicrophoneEnabled: false }));
          }
        };
      } catch (error) {
        logger.error("Error checking microphone permission:", error);
      }
    };

    checkMicrophonePermission();
  }, [requestMicrophoneAccess, state.isMicrophoneEnabled, isDisabled]);

  // Final cleanup useEffect for when the hook instance is unmounted.
  useEffect(() => {
    const componentIsUnmounting = true; // Flag to indicate this is the true unmount cleanup
    return () => {
      logger.info("useVoiceAgent: Main cleanup on unmount running. componentIsUnmounting=", componentIsUnmounting);

      // Get the latest state directly if possible, or rely on the closure state for isStreaming.
      // In StrictMode, this cleanup runs twice. The state.isStreaming here is from the closure of the initial render.
      if (isStreamingRef.current) {
        logger.info("useVoiceAgent: Main unmount: isStreamingRef.current was true, calling stopStreamingAudio.");
        stopStreamingAudio(); // This will also attempt to clean up streaming-specific resources
      } else {
        logger.info("useVoiceAgent: Main unmount: isStreamingRef.current was false. Performing manual resource cleanup if any refs exist.");
        // If not streaming, but resources might exist (e.g., from a failed start or incomplete cleanup)
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(track => track.stop());
          mediaStreamRef.current = null;
        }
        if (audioSourceNodeRef.current) {
          audioSourceNodeRef.current.disconnect();
          audioSourceNodeRef.current = null;
        }
        if (scriptProcessorNodeRef.current) {
          scriptProcessorNodeRef.current.disconnect();
          scriptProcessorNodeRef.current = null;
        }
      }

      // Close input AudioContext
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        logger.info("useVoiceAgent: Main unmount: Closing input AudioContext (audioContextRef).");
        audioContextRef.current.close().catch(e => logger.error("Error closing input AudioContext:", e));
        audioContextRef.current = null;
      }

      // Close output AudioPlayer (which includes its own AudioContext)
      if (audioPlayerRef.current) {
        logger.info("useVoiceAgent: Main unmount: Closing output AudioPlayer (audioPlayerRef).");
        audioPlayerRef.current.close().catch(e => logger.error("Error closing AudioPlayer:", e));
        audioPlayerRef.current = null;
      }

      // Disconnect WebSocket
      if (webSocketService.getConnectionStatus() !== 'disconnected') {
        logger.info("useVoiceAgent: Main unmount: Disconnecting WebSocket service.");
        webSocketService.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array: runs only on mount and unmount of the hook itself.

  // Return no-op functions if disabled, but still preserve state structure
  if (isDisabled) {
    return {
      connectionStatus: 'disconnected' as WebSocketConnectionStatus,
      isStreaming: false,
      chatMessages: [],
      isMicrophoneEnabled: false,
      sessionInitialized: false,
      isUserListening: false,
      isAssistantThinking: false,
      currentTurnId: null,
      connect: () => { logger.info("Voice agent disabled - connect() no-op"); },
      disconnect: () => { logger.info("Voice agent disabled - disconnect() no-op"); },
      clearChatMessages: () => { logger.info("Voice agent disabled - clearChatMessages() no-op"); },
      requestMicrophoneAccess: () => {
        logger.info("Voice agent disabled - requestMicrophoneAccess() no-op");
        return Promise.resolve(false);
      },
      startStreamingAudio: () => { logger.info("Voice agent disabled - startStreamingAudio() no-op"); },
      stopStreamingAudio: () => { logger.info("Voice agent disabled - stopStreamingAudio() no-op"); },
    };
  }

  return {
    ...state,
    chatMessages: state.chatMessages,
    connect,
    disconnect,
    clearChatMessages,
    requestMicrophoneAccess,
    startStreamingAudio,
    stopStreamingAudio,
  };
};

export default useVoiceAgent;
